import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import type {
    TFocusEventHandler,
    TChangeEventHandler,
    TKeyboardEventHandler,
    TInputType,
} from '@alfa-bank/site-input/models';

import type { TEmbossing } from './models';
import {
    validateSymbols,
    deleteStartSymbol,
    deleteEndSymbol,
    addSpace,
    isSuccess,
    useName,
    deleteDoubleSymbols,
    useDefaultEmbossingName,
} from './helpers';

/**
 * Инпут ввода имени для карты. Дефолтное значение берёт из ФИО
 */
export const Embossing: FC<TEmbossing> = ({
    Field,
    Input,
    className,
    getCaption,
    formRole,
    isError,
    autoComplete = 'nope',
    name = 'embossingName',
    settings = {},
    validateNotEmpty,
    isRequired = true,
    hidden,
    styles,
}) => {
    const firstName = useName('firstName');
    const lastName = useName('lastName');

    const defaultValue = useDefaultEmbossingName({
        firstName,
        lastName,
        maxLength: settings.maxLength,
    });

    return (
        <Field<string>
            data={settings}
            defaultValue={defaultValue}
            dispatchesFocus
            name={name}
            receivesFocus
            render={({ input, meta }) => {
                if (formRole === 'actionSetter') {
                    actions.updateAction(meta, { [name]: input.value });
                }

                const validateEndValue = (value: string | number) => {
                    const newValue = deleteEndSymbol(String(value));
                    input.onChange(newValue);
                };

                const handleBlur: TFocusEventHandler = ({ event, value }) => {
                    validateEndValue(value);
                    input.onBlur(event);
                };

                const handleChange: TChangeEventHandler = ({ value }) => {
                    const newValue = addSpace(
                        deleteDoubleSymbols(
                            deleteStartSymbol(validateSymbols(String(value).toUpperCase()))
                        )
                    );

                    actions.onChange(input)(newValue);
                };

                const handleKeyDown: TKeyboardEventHandler = ({ event, value }) => {
                    if (event.key === 'Enter') {
                        validateEndValue(value);
                    }
                };

                return (
                    <div
                        className={cn(className, {
                            [styles.hidden]: hidden,
                        })}
                    >
                        <Input
                            {...input}
                            autoComplete={autoComplete}
                            caption={getCaption(meta, settings.caption)}
                            error={isError(meta)}
                            forwardedRef={actions.getRef(meta)}
                            maxLength={settings.maxLength}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            onFocus={actions.onFocus(input)}
                            onKeyDown={handleKeyDown}
                            placeholder={settings.placeholder}
                            success={isSuccess(meta, firstName, lastName)}
                            type={input.type as TInputType}
                        />
                    </div>
                );
            }}
            validate={isRequired ? validateNotEmpty() : undefined}
        />
    );
};
